import React, { Fragment } from 'react';

import styles from './recommendedUserCardStyles';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import MemberIndexCard from 'buyside/components/presentation/MembersIndexPage/MembersIndexCard/membersIndexCard';
import MemberIndexCardLoading from 'buyside/components/presentation/MembersIndexPage/MembersIndexCard/membersIndexCardLoading';
import PencilIcon from 'buyside/svgs/pencil.svg';
import RecommendationCard, {
  RecommendationCardHeader,
  RecommendationItems,
  RecommendationCardActionsArea as ActionsArea
} from 'buyside/components/base/Card/RecommendationCard';
import Skeleton, { SKELETON_WIDTHS } from 'buyside/components/presentation/Skeleton/skeleton';

import { onboardingUrl } from 'buyside/util/urlHelper';
import { useIsMobile } from 'buyside/util/hooks/useIsMobile';

export const RecommendationLoading = () => (
  <div>
    <RecommendationCard>
      <div style={{ flex: 4 }}>
        <RecommendationCardHeader title="Recommended to you based on:" />
        <RecommendationItems>
          {({ itemStyle }) => (
            <div className={itemStyle}>
              <Skeleton
                containerHeight={20}
                containerWidth={350}
                customClass={styles.skeletonReason}
              />
            </div>
          )}
        </RecommendationItems>
      </div>
      <ActionsArea>
        <Skeleton width={SKELETON_WIDTHS.MEDIUM} />
      </ActionsArea>
    </RecommendationCard>
    <div className={styles.memberCardLoading}>
      <MemberIndexCardLoading />
    </div>
  </div>
);

const Reason = ({ shouldRender, children, className }) => {
  if (shouldRender) {
    return (
      <div className={className}>
        <span>{children}</span>
      </div>
    );
  }
  return null;
};

const RecommendationReasons = ({ user, recommendationStyle }) => {
  const {
    currentRanksCount,
    watchlistIdeasCount,
    wroteOnSameEntitiesCount,
    wroteOnWatchlistedSectors,
    userInWatchlistedGeography
  } = user;
  return (
    <Fragment>
      <Reason shouldRender={currentRanksCount > 0} className={recommendationStyle}>
        <span className={styles.recommendationReasonNumber}>Ranked</span> in{' '}
        <span className={styles.recommendationReasonNumber}>{currentRanksCount}</span> categories
      </Reason>
      <Reason shouldRender={watchlistIdeasCount > 0} className={recommendationStyle}>
        <span className={styles.recommendationReasonNumber}>Wrote {watchlistIdeasCount}</span>{' '}
        idea(s) on watchlisted entities
      </Reason>
      <Reason shouldRender={wroteOnSameEntitiesCount > 0} className={recommendationStyle}>
        <span className={styles.recommendationReasonNumber}>Wrote {wroteOnSameEntitiesCount}</span>{' '}
        idea(s) on entities you wrote about
      </Reason>
      <Reason shouldRender={wroteOnWatchlistedSectors > 0} className={recommendationStyle}>
        <span className={styles.recommendationReasonNumber}>Wrote {wroteOnWatchlistedSectors}</span>{' '}
        idea(s) on watchlisted sectors
      </Reason>
      <Reason shouldRender={userInWatchlistedGeography === true} className={recommendationStyle}>
        <span className={styles.recommendationReasonNumber}>From</span> watchlisted geographies
      </Reason>
    </Fragment>
  );
};

const RecommendedUserCard = ({ user, canViewUserDetails }) => {
  const isMobile = useIsMobile();

  return (
    <div>
      <RecommendationCard>
        <div style={{ flex: 4 }}>
          <RecommendationCardHeader title="Recommended to you based on:" />
          <RecommendationItems>
            {({ itemStyle }) => (
              <RecommendationReasons user={user} recommendationStyle={itemStyle} />
            )}
          </RecommendationItems>
        </div>
        <ActionsArea>
          <Button
            text="Edit watchlist"
            size={BUTTON_SIZES.TINY}
            className={styles.actionButton}
            type={BUTTON_TYPES.LINK}
            icon={PencilIcon}
            url={onboardingUrl()}
          />
        </ActionsArea>
      </RecommendationCard>
      <div className={styles.recommendedUserCardStyle}>
        <MemberIndexCard
          membersIndexCardStyle={styles.membersIndexCardStyle}
          isMobile={isMobile}
          node={{
            city: user.profile.city,
            imageProfileUrl: user.profile.imageProfileUrl,
            summary: user.profile.summary,
            user
          }}
          canViewUserDetails={canViewUserDetails}
        />
      </div>
    </div>
  );
};

export default RecommendedUserCard;
